import {
    Heading,
    Text,
    Stack,
    Container,
    ContainerSize,
    StackBehavior,
    StackDirection,
    Gap,
    JustifyContent,
    AlignItems,
    StackWrap,
    AlignSelf,
    Order,
    Grow,
    Shrink,
    Fill
} from "@rebootui";
import CodeSnippet from "../../../components/controls/CodeSnippet";
import CodePreview from "../../../components/controls/CodePreview";
import CodeHighlighter from "../../../components/controls/CodeHighlighter";

type StackPageProps = {};

const StackPage = ({ }: StackPageProps) => {
    return (
        <Container size={ContainerSize.Fluid} className="p-2">
            <Heading el="h1" className="fw-semibold mb-3 ls-1">
                Stack
            </Heading>
            <Text className="mb-4">
                Effortlessly manage the arrangement, positioning, and dimensions of grid columns, navigation, and
                various components with a full range of responsive flexbox utilities. For more intricate designs,
                additional custom CSS may be needed.
            </Text>

            {/* Stack Behaviors */}
            <Heading el="h2" className="fw-medium fs-3 mt-4 mb-3">
                Stack behaviors
            </Heading>
            <Text className="mb-4">
                Use <Text el="strong">Stack</Text> component to enable a flexbox container, turning its direct child
                elements into flex items. These containers and items can be further customized using various flex
                properties for enhanced layout control. Pass <Text el="code">className</Text> to <Text
                    el="code">Stack</Text> or <Text el="code">Stack.Item</Text> for your own CSS (the examples use
                Bootstrap utilities so you can see the flex areas).
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Stack className="border bg-light">
                        <Stack.Item className="w-100 p-3 border border-success bg-success bg-opacity-10">
                            {"I'm a flexbox container!"}
                        </Stack.Item>
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {StackCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Text className="mb-4">
                The <Text el="strong">Behavior</Text> property is used to define how a stack behaves in terms of layout.
                When set to StackBehavior.InlineFlex, it applies a layout behavior similar to Flexbox, where items are
                arranged inline (horizontally) within a container, but still can wrap and grow/shrink based on the
                available space.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Stack behavior={StackBehavior.InlineFlex} className="border bg-light">
                        <Stack.Item className="w-100 p-3 border border-success bg-success bg-opacity-10">
                            {"I'm a flexbox inline container!"}
                        </Stack.Item>
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {StackBehaviorInlineCode}
                </CodeHighlighter>
            </CodeSnippet>

            {/* Direction */}
            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Direction
            </Heading>
            <Text className="mb-4">
                The direction of flex items inside a flex container can be controlled as needed with the help of
                Direction property. By default, browsers set the direction to `row`, so specifying it is often
                unnecessary. However, in certain cases—such as responsive designs—explicitly defining the direction can
                be beneficial.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Stack className="border bg-light mb-3">
                        <Stack.Item className="p-3 border border-success bg-success bg-opacity-10">Stack
                            item 1</Stack.Item>
                        <Stack.Item className="p-3 border border-success bg-success bg-opacity-10">Stack
                            item 2</Stack.Item>
                        <Stack.Item className="p-3 border border-success bg-success bg-opacity-10">Stack
                            item 3</Stack.Item>
                    </Stack>
                    <Stack direction={StackDirection.RowReverse} className="border bg-light">
                        <Stack.Item className="p-3 border border-success bg-success bg-opacity-10">Stack
                            item 1</Stack.Item>
                        <Stack.Item className="p-3 border border-success bg-success bg-opacity-10">Stack
                            item 2</Stack.Item>
                        <Stack.Item className="p-3 border border-success bg-success bg-opacity-10">Stack
                            item 3</Stack.Item>
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {StackDirectionRowCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Text className="mb-4">
                Set the flex direction to vertical using <Text el="code"
                    className="fw-medium fs-6">{`direction={StackDirection.Column}`}</Text>,
                or reverse the order by using <Text el="code"
                    className="fw-medium fs-6">{`direction={StackDirection.ColumnReverse}`}</Text> to
                start from the opposite side.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Stack direction={StackDirection.Column}
                        className="border bg-light mb-3">
                        <Stack.Item className="p-3 border border-success bg-success bg-opacity-10">Stack
                            item 1</Stack.Item>
                        <Stack.Item className="p-3 border border-success bg-success bg-opacity-10">Stack
                            item 2</Stack.Item>
                        <Stack.Item className="p-3 border border-success bg-success bg-opacity-10">Stack
                            item 3</Stack.Item>
                    </Stack>
                    <Stack direction={StackDirection.ColumnReverse}
                        className="border bg-light">
                        <Stack.Item className="p-3 border border-success bg-success bg-opacity-10">Stack
                            item 1</Stack.Item>
                        <Stack.Item className="p-3 border border-success bg-success bg-opacity-10">Stack
                            item 2</Stack.Item>
                        <Stack.Item className="p-3 border border-success bg-success bg-opacity-10">Stack
                            item 3</Stack.Item>
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {StackDirectionColumnCode}
                </CodeHighlighter>
            </CodeSnippet>

            {/* Gap */}
            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Gap
            </Heading>
            <Text className="mb-4">
                Use the <Text el="code">gap</Text> property to add spacing between flex items. Gap utilities are responsive and can be customized per breakpoint.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Stack gap={Gap.Gap3} className="border bg-light mb-3">
                        <Stack.Item className="p-3 border border-success bg-success bg-opacity-10">Item 1</Stack.Item>
                        <Stack.Item className="p-3 border border-success bg-success bg-opacity-10">Item 2</Stack.Item>
                        <Stack.Item className="p-3 border border-success bg-success bg-opacity-10">Item 3</Stack.Item>
                    </Stack>
                    <Stack direction={StackDirection.Column} gap={Gap.Gap2} className="border bg-light">
                        <Stack.Item className="p-3 border border-success bg-success bg-opacity-10">Item 1</Stack.Item>
                        <Stack.Item className="p-3 border border-success bg-success bg-opacity-10">Item 2</Stack.Item>
                        <Stack.Item className="p-3 border border-success bg-success bg-opacity-10">Item 3</Stack.Item>
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {GapCode}
                </CodeHighlighter>
            </CodeSnippet>

            {/* Justify content */}
            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Justify Content
            </Heading>
            <Text className="mb-4">
                Use <Text el="code">justifyContent</Text> utilities to change the alignment of flex items on the main axis. Choose from <Text el="code">Start</Text>, <Text el="code">Center</Text>, <Text el="code">End</Text>, <Text el="code">Between</Text>, <Text el="code">Around</Text>, or <Text el="code">Evenly</Text>.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <div className="mb-2">
                        <Text className="small text-muted mb-1">Start</Text>
                        <Stack justifyContent={JustifyContent.Start} className="border bg-light">
                            <Stack.Item className="p-3 border border-success bg-success bg-opacity-10">Item 1</Stack.Item>
                            <Stack.Item className="p-3 border border-success bg-success bg-opacity-10">Item 2</Stack.Item>
                        </Stack>
                    </div>
                    <div className="mb-2">
                        <Text className="small text-muted mb-1">Center</Text>
                        <Stack justifyContent={JustifyContent.Center} className="border bg-light">
                            <Stack.Item className="p-3 border border-success bg-success bg-opacity-10">Item 1</Stack.Item>
                            <Stack.Item className="p-3 border border-success bg-success bg-opacity-10">Item 2</Stack.Item>
                        </Stack>
                    </div>
                    <div className="mb-2">
                        <Text className="small text-muted mb-1">End</Text>
                        <Stack justifyContent={JustifyContent.End} className="border bg-light">
                            <Stack.Item className="p-3 border border-success bg-success bg-opacity-10">Item 1</Stack.Item>
                            <Stack.Item className="p-3 border border-success bg-success bg-opacity-10">Item 2</Stack.Item>
                        </Stack>
                    </div>
                    <div className="mb-2">
                        <Text className="small text-muted mb-1">Between</Text>
                        <Stack justifyContent={JustifyContent.Between} className="border bg-light">
                            <Stack.Item className="p-3 border border-success bg-success bg-opacity-10">Item 1</Stack.Item>
                            <Stack.Item className="p-3 border border-success bg-success bg-opacity-10">Item 2</Stack.Item>
                        </Stack>
                    </div>
                    <div className="mb-2">
                        <Text className="small text-muted mb-1">Around</Text>
                        <Stack justifyContent={JustifyContent.Around} className="border bg-light">
                            <Stack.Item className="p-3 border border-success bg-success bg-opacity-10">Item 1</Stack.Item>
                            <Stack.Item className="p-3 border border-success bg-success bg-opacity-10">Item 2</Stack.Item>
                        </Stack>
                    </div>
                    <div>
                        <Text className="small text-muted mb-1">Evenly</Text>
                        <Stack justifyContent={JustifyContent.Evenly} className="border bg-light">
                            <Stack.Item className="p-3 border border-success bg-success bg-opacity-10">Item 1</Stack.Item>
                            <Stack.Item className="p-3 border border-success bg-success bg-opacity-10">Item 2</Stack.Item>
                        </Stack>
                    </div>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {JustifyContentCode}
                </CodeHighlighter>
            </CodeSnippet>

            {/* Align Items */}
            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Align Items
            </Heading>
            <Text className="mb-4">
                Use <Text el="code">alignItems</Text> utilities to change the alignment of flex items on the cross axis.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Stack alignItems={AlignItems.Start} className="border bg-light mb-2" style={{ minHeight: '100px' }}>
                        <Stack.Item className="p-3 border border-success bg-success bg-opacity-10">Item 1</Stack.Item>
                        <Stack.Item className="p-3 border border-success bg-success bg-opacity-10">Item 2</Stack.Item>
                    </Stack>
                    <Stack alignItems={AlignItems.Center} className="border bg-light mb-2" style={{ minHeight: '100px' }}>
                        <Stack.Item className="p-3 border border-success bg-success bg-opacity-10">Item 1</Stack.Item>
                        <Stack.Item className="p-3 border border-success bg-success bg-opacity-10">Item 2</Stack.Item>
                    </Stack>
                    <Stack alignItems={AlignItems.End} className="border bg-light" style={{ minHeight: '100px' }}>
                        <Stack.Item className="p-3 border border-success bg-success bg-opacity-10">Item 1</Stack.Item>
                        <Stack.Item className="p-3 border border-success bg-success bg-opacity-10">Item 2</Stack.Item>
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {AlignItemsCode}
                </CodeHighlighter>
            </CodeSnippet>

            {/* Wrap */}
            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Wrap
            </Heading>
            <Text className="mb-4">
                Control how flex items wrap within the container using the <Text el="code">wrap</Text> property.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Stack wrap={StackWrap.Wrap} className="border bg-light" style={{ maxWidth: '300px' }}>
                        <Stack.Item className="p-3 border border-success bg-success bg-opacity-10" style={{ width: '150px' }}>Item 1</Stack.Item>
                        <Stack.Item className="p-3 border border-success bg-success bg-opacity-10" style={{ width: '150px' }}>Item 2</Stack.Item>
                        <Stack.Item className="p-3 border border-success bg-success bg-opacity-10" style={{ width: '150px' }}>Item 3</Stack.Item>
                        <Stack.Item className="p-3 border border-success bg-success bg-opacity-10" style={{ width: '150px' }}>Item 4</Stack.Item>
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {WrapCode}
                </CodeHighlighter>
            </CodeSnippet>

            {/* Align Self */}
            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Align Self
            </Heading>
            <Text className="mb-4">
                Use <Text el="code">alignSelf</Text> on individual flex items to override the container's <Text el="code">alignItems</Text> value.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Stack className="border bg-light" style={{ minHeight: '200px' }}>
                        <Stack.Item alignSelf={AlignSelf.Start} className="p-3 border border-success bg-success bg-opacity-10">Aligned start</Stack.Item>
                        <Stack.Item alignSelf={AlignSelf.Center} className="p-3 border border-success bg-success bg-opacity-10">Aligned center</Stack.Item>
                        <Stack.Item alignSelf={AlignSelf.End} className="p-3 border border-success bg-success bg-opacity-10">Aligned end</Stack.Item>
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {AlignSelfCode}
                </CodeHighlighter>
            </CodeSnippet>

            {/* Order */}
            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Order
            </Heading>
            <Text className="mb-4">
                Change the visual order of flex items with <Text el="code">order</Text>. Use <Text el="code">Order.First</Text> or <Text el="code">Order.Last</Text> for quick reordering.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Stack className="border bg-light mb-3">
                        <Stack.Item order={Order.Order3} className="p-3 border border-success bg-success bg-opacity-10">First in DOM (order 3)</Stack.Item>
                        <Stack.Item className="p-3 border border-success bg-success bg-opacity-10">Second in DOM (no order)</Stack.Item>
                        <Stack.Item order={Order.Order1} className="p-3 border border-success bg-success bg-opacity-10">Third in DOM (order 1)</Stack.Item>
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {OrderCode}
                </CodeHighlighter>
            </CodeSnippet>

            {/* Grow and Shrink */}
            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Grow and Shrink
            </Heading>
            <Text className="mb-4">
                Use <Text el="code">grow</Text> and <Text el="code">shrink</Text> props to control how flex items grow or shrink to fill available space.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Stack className="border bg-light mb-3">
                        <Stack.Item grow={Grow.Grow1} className="p-3 border border-success bg-success bg-opacity-10">Grow 1</Stack.Item>
                        <Stack.Item className="p-3 border border-success bg-success bg-opacity-10">Normal</Stack.Item>
                        <Stack.Item className="p-3 border border-success bg-success bg-opacity-10">Normal</Stack.Item>
                    </Stack>
                    <Stack className="border bg-light mb-3">
                        <Stack.Item shrink={Shrink.Shrink0} className="p-3 border border-success bg-success bg-opacity-10" style={{ width: '300px' }}>No shrink (width: 300px)</Stack.Item>
                        <Stack.Item className="p-3 border border-success bg-success bg-opacity-10">Will shrink if needed</Stack.Item>
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {GrowShrinkCode}
                </CodeHighlighter>
            </CodeSnippet>

            {/* Fill */}
            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Fill
            </Heading>
            <Text className="mb-4">
                Use <Text el="code">fill</Text> on flex items to force them into equal widths (or heights in a column layout).
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Stack className="border bg-light mb-3">
                        <Stack.Item fill={Fill.Fill} className="p-3 border border-success bg-success bg-opacity-10">Equal width item</Stack.Item>
                        <Stack.Item fill={Fill.Fill} className="p-3 border border-success bg-success bg-opacity-10">Equal width item</Stack.Item>
                        <Stack.Item fill={Fill.Fill} className="p-3 border border-success bg-success bg-opacity-10">Equal width item with more content</Stack.Item>
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {FillCode}
                </CodeHighlighter>
            </CodeSnippet>
        </Container>
    );
};

export default StackPage;

// Code snippets
const StackCode = `<Stack className="border bg-light">
    <Stack.Item className="w-100 p-3 border border-success bg-success bg-opacity-10">
        I'm a flexbox container!
    </Stack.Item>
</Stack>`;

const StackBehaviorInlineCode = `<Stack behavior={StackBehavior.InlineFlex} className="border bg-light">
    <Stack.Item className="w-100 p-3 border border-success bg-success bg-opacity-10">
        I'm a flexbox inline container!
    </Stack.Item>
</Stack>`;

const stackDemoItemClass = `className="p-3 border border-success bg-success bg-opacity-10"`;

const StackDirectionRowCode = `<Stack className="border bg-light mb-3">
    <Stack.Item ${stackDemoItemClass}>Stack item 1</Stack.Item>
    <Stack.Item ${stackDemoItemClass}>Stack item 2</Stack.Item>
    <Stack.Item ${stackDemoItemClass}>Stack item 3</Stack.Item>
</Stack>

<Stack direction={StackDirection.RowReverse} className="border bg-light">
    <Stack.Item ${stackDemoItemClass}>Stack item 1</Stack.Item>
    <Stack.Item ${stackDemoItemClass}>Stack item 2</Stack.Item>
    <Stack.Item ${stackDemoItemClass}>Stack item 3</Stack.Item>
</Stack>`;

const StackDirectionColumnCode = `<Stack direction={StackDirection.Column} className="border bg-light mb-3">
    <Stack.Item ${stackDemoItemClass}>Stack item 1</Stack.Item>
    <Stack.Item ${stackDemoItemClass}>Stack item 2</Stack.Item>
    <Stack.Item ${stackDemoItemClass}>Stack item 3</Stack.Item>
</Stack>

<Stack direction={StackDirection.ColumnReverse} className="border bg-light">
    <Stack.Item ${stackDemoItemClass}>Stack item 1</Stack.Item>
    <Stack.Item ${stackDemoItemClass}>Stack item 2</Stack.Item>
    <Stack.Item ${stackDemoItemClass}>Stack item 3</Stack.Item>
</Stack>`;

const GapCode = `<Stack gap={Gap.Gap3} className="border bg-light mb-3">
    <Stack.Item ${stackDemoItemClass}>Item 1</Stack.Item>
    <Stack.Item ${stackDemoItemClass}>Item 2</Stack.Item>
    <Stack.Item ${stackDemoItemClass}>Item 3</Stack.Item>
</Stack>

<Stack direction={StackDirection.Column} gap={Gap.Gap2} className="border bg-light">
    <Stack.Item ${stackDemoItemClass}>Item 1</Stack.Item>
    <Stack.Item ${stackDemoItemClass}>Item 2</Stack.Item>
    <Stack.Item ${stackDemoItemClass}>Item 3</Stack.Item>
</Stack>`;

const JustifyContentCode = `<Stack justifyContent={JustifyContent.Start} className="border bg-light">
    <Stack.Item ${stackDemoItemClass}>Item 1</Stack.Item>
    <Stack.Item ${stackDemoItemClass}>Item 2</Stack.Item>
</Stack>

<Stack justifyContent={JustifyContent.Center} className="border bg-light">
    <Stack.Item ${stackDemoItemClass}>Item 1</Stack.Item>
    <Stack.Item ${stackDemoItemClass}>Item 2</Stack.Item>
</Stack>

<Stack justifyContent={JustifyContent.End} className="border bg-light">
    <Stack.Item ${stackDemoItemClass}>Item 1</Stack.Item>
    <Stack.Item ${stackDemoItemClass}>Item 2</Stack.Item>
</Stack>

<Stack justifyContent={JustifyContent.Between} className="border bg-light">
    <Stack.Item ${stackDemoItemClass}>Item 1</Stack.Item>
    <Stack.Item ${stackDemoItemClass}>Item 2</Stack.Item>
</Stack>

<Stack justifyContent={JustifyContent.Around} className="border bg-light">
    <Stack.Item ${stackDemoItemClass}>Item 1</Stack.Item>
    <Stack.Item ${stackDemoItemClass}>Item 2</Stack.Item>
</Stack>

<Stack justifyContent={JustifyContent.Evenly} className="border bg-light">
    <Stack.Item ${stackDemoItemClass}>Item 1</Stack.Item>
    <Stack.Item ${stackDemoItemClass}>Item 2</Stack.Item>
</Stack>`;

const AlignItemsCode = `<Stack alignItems={AlignItems.Start} className="border bg-light mb-2" style={{ minHeight: '100px' }}>
    <Stack.Item ${stackDemoItemClass}>Item 1</Stack.Item>
    <Stack.Item ${stackDemoItemClass}>Item 2</Stack.Item>
</Stack>

<Stack alignItems={AlignItems.Center} className="border bg-light mb-2" style={{ minHeight: '100px' }}>
    <Stack.Item ${stackDemoItemClass}>Item 1</Stack.Item>
    <Stack.Item ${stackDemoItemClass}>Item 2</Stack.Item>
</Stack>

<Stack alignItems={AlignItems.End} className="border bg-light" style={{ minHeight: '100px' }}>
    <Stack.Item ${stackDemoItemClass}>Item 1</Stack.Item>
    <Stack.Item ${stackDemoItemClass}>Item 2</Stack.Item>
</Stack>`;

const WrapCode = `<Stack wrap={StackWrap.Wrap} className="border bg-light" style={{ maxWidth: '300px' }}>
    <Stack.Item ${stackDemoItemClass} style={{ width: '150px' }}>Item 1</Stack.Item>
    <Stack.Item ${stackDemoItemClass} style={{ width: '150px' }}>Item 2</Stack.Item>
    <Stack.Item ${stackDemoItemClass} style={{ width: '150px' }}>Item 3</Stack.Item>
    <Stack.Item ${stackDemoItemClass} style={{ width: '150px' }}>Item 4</Stack.Item>
</Stack>`;

const AlignSelfCode = `<Stack className="border bg-light" style={{ minHeight: '200px' }}>
    <Stack.Item alignSelf={AlignSelf.Start} ${stackDemoItemClass}>Aligned start</Stack.Item>
    <Stack.Item alignSelf={AlignSelf.Center} ${stackDemoItemClass}>Aligned center</Stack.Item>
    <Stack.Item alignSelf={AlignSelf.End} ${stackDemoItemClass}>Aligned end</Stack.Item>
</Stack>`;

const OrderCode = `<Stack className="border bg-light mb-3">
    <Stack.Item order={Order.Order3} ${stackDemoItemClass}>First in DOM (order 3)</Stack.Item>
    <Stack.Item ${stackDemoItemClass}>Second in DOM (no order)</Stack.Item>
    <Stack.Item order={Order.Order1} ${stackDemoItemClass}>Third in DOM (order 1)</Stack.Item>
</Stack>`;

const GrowShrinkCode = `<Stack className="border bg-light mb-3">
    <Stack.Item grow={Grow.Grow1} ${stackDemoItemClass}>Grow 1</Stack.Item>
    <Stack.Item ${stackDemoItemClass}>Normal</Stack.Item>
    <Stack.Item ${stackDemoItemClass}>Normal</Stack.Item>
</Stack>

<Stack className="border bg-light mb-3">
    <Stack.Item shrink={Shrink.Shrink0} ${stackDemoItemClass} style={{ width: '300px' }}>No shrink (width: 300px)</Stack.Item>
    <Stack.Item ${stackDemoItemClass}>Will shrink if needed</Stack.Item>
</Stack>`;

const FillCode = `<Stack className="border bg-light mb-3">
    <Stack.Item fill={Fill.Fill} ${stackDemoItemClass}>Equal width item</Stack.Item>
    <Stack.Item fill={Fill.Fill} ${stackDemoItemClass}>Equal width item</Stack.Item>
    <Stack.Item fill={Fill.Fill} ${stackDemoItemClass}>Equal width item with more content</Stack.Item>
</Stack>`;
